/**
 * Status semantics, decoupled from specific UUIDs.
 *
 * The app used to key behaviour on hardcoded status ids in constants.ts —
 * BLOCKED_STATUS_ID, DONE_STATUS_ID, and the INACTIVE / COMPLETION /
 * HIDDEN_WHEN_HIDE_DONE id lists. That only works while every deployment shares
 * one fixed status set. Once a tenant defines its own statuses, those ids are
 * unknown to the build, so the meaning has to travel on the row instead:
 * dropdown_options.kind (see migration 0023).
 *
 * This module is the single place that maps a kind to behaviour. The derived
 * sets reproduce the old id lists exactly, so moving to kind changed no counts:
 *
 *   COMPLETION       = {shipped, done}
 *   INACTIVE         = {shipped, done, on_hold, denied}
 *   HIDDEN_WHEN_DONE = {done, denied}
 */

/** The behaviour category of a status option. NULL kinds (non-status options,
 *  or unclassified custom statuses) behave as plain active statuses. */
export type StatusKind = "todo" | "active" | "blocked" | "on_hold" | "shipped" | "done" | "rejected" | "denied";

/** The kinds, with human labels, for the status-management picker. The order is
 *  roughly the lifecycle order a workflow reads in. */
export const STATUS_KINDS: ReadonlyArray<{ value: StatusKind; label: string }> = [
  { value: "todo", label: "To do" },
  { value: "active", label: "Active" },
  { value: "blocked", label: "Blocked" },
  { value: "on_hold", label: "On hold" },
  { value: "shipped", label: "Shipped" },
  { value: "done", label: "Done" },
  { value: "rejected", label: "Rejected" },
  { value: "denied", label: "Denied" },
];

/** A status is "completed" for time-to-prod / completion metrics. */
const COMPLETION_KINDS: ReadonlySet<StatusKind> = new Set<StatusKind>(["shipped", "done"]);

/** A status is "not active" — excluded from active-item counts. */
const INACTIVE_KINDS: ReadonlySet<StatusKind> = new Set<StatusKind>(["shipped", "done", "on_hold", "denied"]);

/** A status is hidden from the main backlog when the "Hide Done" toggle is on. */
const HIDDEN_WHEN_DONE_KINDS: ReadonlySet<StatusKind> = new Set<StatusKind>(["done", "denied"]);

export const isCompletionKind = (kind: StatusKind | null | undefined): boolean =>
  kind != null && COMPLETION_KINDS.has(kind);

export const isInactiveKind = (kind: StatusKind | null | undefined): boolean =>
  kind != null && INACTIVE_KINDS.has(kind);

export const isHiddenWhenDoneKind = (kind: StatusKind | null | undefined): boolean =>
  kind != null && HIDDEN_WHEN_DONE_KINDS.has(kind);

/** Moving a ticket into this status requires a blocked_reason. */
export const requiresBlockedReason = (kind: StatusKind | null | undefined): boolean => kind === "blocked";

/** Moving a ticket into this status requires a rejected_reason. */
export const requiresRejectedReason = (kind: StatusKind | null | undefined): boolean => kind === "rejected";

/** The two kinds whose UI demands a reason before the move is allowed. */
export const requiresReason = (kind: StatusKind | null | undefined): boolean =>
  requiresBlockedReason(kind) || requiresRejectedReason(kind);

/**
 * Minimal shape needed to reason about a status: its id and its kind.
 *
 * `kind` is typed loosely as `string` because it arrives that way from the
 * generated DB types; the dropdown_options_kind_check constraint guarantees it
 * is a valid StatusKind or null, so the lookup narrows it back on the way out.
 */
export interface StatusKinded {
  id: string;
  kind?: string | null;
}

/**
 * Build an id → kind lookup from a loaded status list.
 *
 * Consumers that only hold a ticket's `status_id` (not the status row) use this
 * to recover the kind, replacing the old `SOME_STATUS_IDS.includes(status_id)`.
 */
export function statusKindLookup(
  statuses: readonly StatusKinded[],
): (statusId: string | null | undefined) => StatusKind | null {
  const byId = new Map<string, StatusKind | null>(statuses.map((s) => [s.id, (s.kind ?? null) as StatusKind | null]));
  return (statusId) => (statusId ? (byId.get(statusId) ?? null) : null);
}

/**
 * The status a bulk "mark as done" action should target, resolved from the
 * tenant's own status set rather than a hardcoded DONE_STATUS_ID. Prefers a
 * `done` status; falls back to a `shipped` one if the tenant has no `done`.
 */
export function resolveDoneStatusId(statuses: readonly StatusKinded[]): string | undefined {
  return (statuses.find((s) => s.kind === "done") ?? statuses.find((s) => s.kind === "shipped"))?.id;
}
